/**
 * Property sub-reducer — handles properties/apply, chprop/apply,
 * matchprop/set-source, matchprop/apply commands.
 * Extracted from editorStore.ts — pure structural refactor.
 *
 * Phase 3b PR #1 — migrated to `ShellReducerMutationResult`. The only
 * cases that actually mutate entity rows in `state.drawing.entities`
 * are `properties/apply` (with non-empty entityIds), `chprop/apply`
 * (delegates to properties/apply), and `matchprop/apply`. They emit
 * `mutations.updated` with EntityPreview prev/next pairs sourced from
 * `PropertyMutationResult.updatedEntityIds` (the property mutators'
 * own bookkeeping; `applyPropertyResults` calls Zod parse which breaks
 * referential identity, so id-driven lookup is required). Cases that
 * only touch shell metadata (matchprop/set-source /
 * properties/apply with empty entityIds / matchprop without source /
 * matchprop with empty targets) emit `NO_SHELL_MUTATIONS`.
 *
 * Returns the next `ShellReducerMutationResult` if the command matched,
 * or `null` if the command type is not handled here.
 */

import {
  isCommonObjectProperty,
  matchProperties,
  mergePropertyValueMap,
  setEntitySpecificProperties,
  setObjectProperties,
  type PropertyMutationResult,
} from "@dxf-ai-editor/command-engine";
import type {
  DynamicBlockCommand,
  EditorCommand,
  EditorShellState,
  EntityPreview,
} from "@dxf-ai-editor/contracts";
import type {
  DrawingDocument,
  DrawingEntity,
} from "@dxf-ai-editor/drawing-model";

import { t } from "../i18n";
import { namesEqualCaseInsensitive } from "./editorStoreInitialState";
import { applyPropertyResults, pushAuditEvent } from "./reducerHelpers";
import {
  isGripDragPayload,
  resolveGripDragDocumentEntity,
  translateGripDrag,
} from "./gripDragTranslate";
import {
  NO_SHELL_MUTATIONS,
  type EntityMutation,
  type ShellReducerMutationResult,
  type StyleTableTouchKind,
} from "./types";

function noShellMutation(state: EditorShellState): ShellReducerMutationResult {
  return { state, mutations: NO_SHELL_MUTATIONS };
}

/**
 * Build the EntityPreview-typed mutation diff from a list of touched
 * entity ids. `updatedEntityIds` comes from `PropertyMutationResult`
 * (the property mutators' own bookkeeping — the canonical source of
 * truth for which rows changed). We can't rely on reference equality
 * here because `applyPropertyResults` -> `pushAuditEvent` calls
 * `editorShellStateSchema.parse(...)`, and Zod's parse always rebuilds
 * fresh objects, breaking referential identity for ALL entities even
 * though only a subset changed semantically. Looking up prev/next by
 * id in the two arrays sidesteps that.
 */
function buildPreviewMutations(
  prevEntities: EntityPreview[],
  nextEntities: EntityPreview[],
  updatedEntityIds: ReadonlyArray<string>,
): EntityMutation<EntityPreview> {
  if (updatedEntityIds.length === 0) return NO_SHELL_MUTATIONS;
  const prevById = new Map<string, EntityPreview>();
  for (const entity of prevEntities) prevById.set(entity.id, entity);
  const nextById = new Map<string, EntityPreview>();
  for (const entity of nextEntities) nextById.set(entity.id, entity);

  const updated: Array<{
    spaceId: string;
    prev: EntityPreview;
    next: EntityPreview;
  }> = [];
  for (const id of updatedEntityIds) {
    const prev = prevById.get(id);
    const next = nextById.get(id);
    // Defensive: a property mutator should never report an id that
    // isn't present in both arrays. Skip rather than throw — preserves
    // legacy behaviour for any edge case where the entity was
    // concurrently removed by another reducer pass.
    if (!prev || !next) continue;
    updated.push({ spaceId: next.spaceId, prev, next });
  }
  if (updated.length === 0) return NO_SHELL_MUTATIONS;
  return {
    added: [],
    removed: [],
    updated,
    styleTablesTouched: new Set<StyleTableTouchKind>(),
  };
}

/** Union the `updatedEntityIds` of multiple PropertyMutationResults. */
function collectUpdatedIds(results: PropertyMutationResult[]): string[] {
  const ids = new Set<string>();
  for (const r of results) for (const id of r.updatedEntityIds) ids.add(id);
  return [...ids];
}

export function reducePropertyCommand(
  state: EditorShellState,
  command: EditorCommand | DynamicBlockCommand,
  // Codex S12b P2 — the LIVE pre-mutation geometry document. Threaded so the
  // grip-drag translator picks the SAME arc-endpoint inverse frame the grip
  // actually RENDERED in: `buildGripSet` sources a selected entity's grips from
  // cad-core (typed WCS, `cy + r·sin`) iff the id resolves to a typed entity,
  // and from the legacy `calculateGrips` Y-mirror (`cy − r·sin`) otherwise. The
  // geometry reducer already keys its `translateGripDrag` frame on the SAME
  // presence; without the same signal here the shell reducer always took the
  // legacy (mirrored) branch for a typed-rendered arc. Normally the geometry
  // resync overwrites the shell, but a no-op geometry drag (e.g. a 90° start
  // grip dropped back on its visible (cx, cy + r)) leaves an EMPTY mutation set
  // → no resync → the mirrored 270° value survives in the shell preview /
  // properties while the doc stays at 90°.
  geometryDocument?: DrawingDocument,
  // Codex S12b P2 (post-merge remediation of #493) — the ONE-TRUTH typed-entity
  // resolver the grip RENDER uses (`resolveTypedEntityById` → `getEntityById`,
  // compact-cache first, `geometryDocument.spaces` fallback). Injected (not
  // imported — propertyReducer is store-free to avoid an editorStore import
  // cycle) so the shell inverse keys its typed-vs-legacy frame on the EXACT same
  // signal `buildGripSet` did. Used via `resolveGripDragDocumentEntity`
  // (gripDragTranslate.ts) as a FALLBACK ONLY for the case a
  // `geometryDocument.spaces` linear scan misses (a large/streamed import where
  // `spaces` is slimmed but the compact cache still holds the arc): the grip
  // renders typed (+Y) yet the scan misses it → without the resolver the legacy
  // Y-mirror branch fires → persistent 270° shell desync on a no-op drag. The
  // scan stays PRIMARY (not the resolver) because `getEntityById` reconstructs a
  // fresh entity from `Float32Array` compact-cache coordinates on every call —
  // preferring it over a scan hit would round high-precision Line/Circle/Arc/
  // LWPOLYLINE/Spline/Point geometry that the full-precision document already
  // had (Codex P2, #515 round 2). When absent (direct-reducer unit callers) the
  // grip-drag branch uses the linear `geometryDocument` scan alone, preserving
  // the pure-reducer contract.
  resolveDocumentEntity?: (id: string) => DrawingEntity | undefined,
): ShellReducerMutationResult | null {
  switch (command.type) {
    case "properties/apply": {
      if (command.entityIds.length === 0) {
        // T7 (agent-bridge), local Codex round-14 P2 — validate a `Layer`
        // current-pointer SET against the LIVE layer table before merging it
        // into `defaultObjectProperties`. This is the ONE mutation site the
        // "no-selection properties/apply" current-pointer mechanic BOTH the
        // human-facing `setCurrentLayer` wrapper (which pre-validates
        // existence before dispatch — see editorStoreStyleTables.ts) AND the
        // AI `SET_ACTIVE_LAYER` translator (deliberately THIN/TOTAL — no
        // document context at translate time, mirroring every other
        // current-pointer AI action — see layerTranslators.ts) route
        // through. Without this guard, an AI-proposed UNKNOWN layer name was
        // written here unvalidated, then the dispatch chokepoint's
        // `reconcileCurrentLayerAgainstTable` generic fallback
        // (editorStoreInitialState.ts) — which exists to catch the TABLE
        // shrinking out from under a previously-valid CLAYER (delete/undo/
        // purge) — ALSO caught this newly-invalid value and reset CLAYER to
        // "0", silently CLOBBERING whatever valid layer the user/agent was
        // previously on (a real regression, not a harmless no-op: the NEXT
        // AI-created entity would land on "0" instead of the actual current
        // layer). Dropping an unknown `Layer` value here (leaving the
        // pointer UNTOUCHED) makes an invalid SET_ACTIVE_LAYER fail CLOSED
        // exactly like the human wrapper, instead of destructively
        // resetting it. A no-op for every EXISTING caller (the human
        // wrapper's value is always already valid by the time it gets
        // here, so this check never trips for it).
        // Local Codex round-23 P2 (local read-only review) — resolve
        // `Layer` case-insensitively against the live table, mirroring
        // `namesEqualCaseInsensitive` everywhere else a layer name is
        // matched (round-21/22). Without this, an AI `SET_ACTIVE_LAYER`
        // naming an EXISTING layer by a different case (e.g. "walls"
        // against a table row "Walls") was treated as UNKNOWN and
        // silently dropped — CLAYER stayed on its old value even though
        // the table clearly has a matching row, and the reducer's own
        // rename/create/collision logic treats that exact pair as ONE
        // layer. `canonicalLayerMatch` carries the row's TRUE stored
        // spelling so `defaultObjectProperties.Layer` always holds the
        // canonical name (same invariant `reconcileCurrentLayerAgainstTable`
        // relies on), not whatever casing the command happened to carry.
        const layerValue = command.values.Layer;
        const canonicalLayerMatch =
          layerValue !== undefined
            ? (geometryDocument?.styleTables?.layers ?? []).find((l) =>
                namesEqualCaseInsensitive(l.name, layerValue),
              )
            : undefined;
        const layerValueIsUnknown =
          layerValue !== undefined &&
          geometryDocument !== undefined &&
          canonicalLayerMatch === undefined;
        const sanitizedValues = layerValueIsUnknown
          ? Object.fromEntries(
              Object.entries(command.values).filter(
                ([key]) => key !== "Layer",
              ),
            )
          : canonicalLayerMatch !== undefined &&
              canonicalLayerMatch.name !== layerValue
            ? { ...command.values, Layer: canonicalLayerMatch.name }
            : command.values;
        if (Object.keys(sanitizedValues).length === 0) {
          return noShellMutation(state);
        }
        const nextState = pushAuditEvent(
          {
            ...state,
            propertyWorkflows: {
              ...state.propertyWorkflows,
              defaultObjectProperties: {
                ...state.propertyWorkflows.defaultObjectProperties,
                ...sanitizedValues,
              },
            },
          },
          command,
          `PROPERTIES: default object properties updated for ${Object.keys(sanitizedValues).join(", ")}`,
          "success",
        );
        return noShellMutation(nextState);
      }

      // GRIPS-SUBSYSTEM (§5.11) — grip-drag commit. StageGripDrag.ts emits
      // the pseudo-properties gripDragWorldX/Y + gripId/gripKind; translate
      // them into the REAL geometry-property patch (Start X / Radius /
      // Vertices / Control points / …) against the dragged entity's current
      // geometry, then fall through to the normal mutation path so the edit
      // commits through the Command Engine and is undoable. A grip whose id
      // is not recognised for the entity type yields no patch → no-op
      // (rather than corrupting the entity with the raw pseudo-keys).
      if (isGripDragPayload(command.values)) {
        const targetId = command.entityIds[0]!;
        const entity = state.drawing.entities.find((e) => e.id === targetId);
        if (!entity) return noShellMutation(state);
        // Codex S12b P2 — resolve the LIVE document entity (full precision, WCS)
        // so `translateGripDrag` selects the arc-endpoint inverse frame that
        // MATCHES the frame the grip rendered in. Present ⟺ the arc rendered its
        // grips from cad-core (WCS, no display Y-flip); absent ⟺ the legacy
        // `calculateGrips` Y-mirror. This keeps the shell preview/properties
        // consistent with the doc even when the geometry drag resolves to a no-op
        // (whose empty mutation set skips the resync that would otherwise correct
        // a mirrored shell value).
        //
        // ONE TRUTH (post-merge remediation of #493, round 2) — resolved through
        // `resolveGripDragDocumentEntity` (gripDragTranslate.ts, shared with the
        // document reducer's `preMutationDocEntity` resolution in
        // geometryReducer.ts): a `geometryDocument.spaces` scan first (full
        // precision, authoritative when it finds the entity), falling back to
        // the injected `resolveDocumentEntity` (`getEntityById`, compact-cache
        // first) ONLY when the scan misses (slimmed `spaces` on a large/streamed
        // import) — so the typed-vs-legacy signal is identical BY CONSTRUCTION
        // between shell and document WITHOUT regressing geometry precision for
        // the common (non-slimmed) case.
        const documentEntity = resolveGripDragDocumentEntity(
          targetId,
          geometryDocument,
          resolveDocumentEntity,
        );
        const realPatch = translateGripDrag(
          entity,
          command.values,
          documentEntity,
        );
        if (!realPatch || Object.keys(realPatch).length === 0) {
          return noShellMutation(state);
        }
        return reducePropertyCommand(
          state,
          {
            type: "properties/apply",
            source: command.source,
            entityIds: command.entityIds,
            values: realPatch,
          },
          geometryDocument,
          resolveDocumentEntity,
        );
      }

      // Phase 4.1.e: when perEntityValues is present (undo/redo inverse),
      // apply per-entity values by collecting all distinct (entityId, values)
      // pairs and routing each through its own sub-dispatch. This handles the
      // heterogeneous case where entities had different old values.
      if (command.perEntityValues && Object.keys(command.perEntityValues).length > 0) {
        const prevEntities = state.drawing.entities;
        let currentState = state;
        for (const entityId of command.entityIds) {
          const entityValues = command.perEntityValues[entityId] ?? command.values;
          if (Object.keys(entityValues).length === 0) continue;
          const subResult = reducePropertyCommand(
            currentState,
            {
              type: "properties/apply",
              source: command.source,
              entityIds: [entityId],
              values: entityValues,
            },
            geometryDocument,
            resolveDocumentEntity,
          );
          if (subResult) {
            currentState = subResult.state;
          }
        }
        // Build unified mutations from all touched entities
        const allTouchedIds = command.entityIds.filter((id) => {
          const prev = prevEntities.find((e) => e.id === id);
          const next = currentState.drawing.entities.find((e) => e.id === id);
          return prev && next && prev !== next;
        });
        const mutations = buildPreviewMutations(
          prevEntities,
          currentState.drawing.entities,
          allTouchedIds,
        );
        return { state: currentState, mutations };
      }

      const changes = mergePropertyValueMap(command.values);
      const commonChanges = changes.filter((change) =>
        isCommonObjectProperty(change.name),
      );
      const specificChanges = changes.filter(
        (change) => !isCommonObjectProperty(change.name),
      );
      const results: PropertyMutationResult[] = [];

      if (commonChanges.length > 0) {
        results.push(
          setObjectProperties({
            entities: state.drawing.entities,
            targetEntityIds: command.entityIds,
            changes: commonChanges,
          }),
        );
      }

      if (specificChanges.length > 0) {
        const previousEntities =
          results.at(-1)?.entities ?? state.drawing.entities;

        results.push(
          setEntitySpecificProperties({
            entities: previousEntities,
            targetEntityIds: command.entityIds,
            changes: specificChanges,
          }),
        );
      }

      const prevEntities = state.drawing.entities;
      const nextState = applyPropertyResults(
        state,
        command,
        results,
        "PROPERTIES",
      );
      const mutations = buildPreviewMutations(
        prevEntities,
        nextState.drawing.entities,
        collectUpdatedIds(results),
      );
      return { state: nextState, mutations };
    }

    case "chprop/apply": {
      // CHPROP is a narrower form of PROPERTIES — both take property
      // changes on a selection and both must flow through the same
      // dual-path pipeline so that common (Layer, Color, Linetype, ...)
      // AND entity-specific (Dimension style, Text override, Text height,
      // ...) changes both land. Delegating to properties/apply here keeps
      // the CHPROP path in perfect sync with PROPERTIES without
      // duplicating the filter/merge logic. Underscores in the property
      // name are normalized to spaces because the CHPROP command-line
      // form uses `Dimension_style` while the editable-properties dictionary
      // keys are `Dimension style`.
      const normalizedProperty = command.property.replace(/_/g, " ");
      return reducePropertyCommand(
        state,
        {
          type: "properties/apply",
          source: command.source,
          entityIds: command.entityIds,
          values: { [normalizedProperty]: command.value },
        },
        geometryDocument,
        resolveDocumentEntity,
      );
    }

    case "matchprop/set-source":
      return noShellMutation(
        pushAuditEvent(
          {
            ...state,
            propertyWorkflows: {
              ...state.propertyWorkflows,
              matchpropSourceEntityId: command.entityId,
            },
          },
          command,
          command.entityId
            ? `MATCHPROP source armed for ${command.entityId}.`
            : t("audit.matchprop_source_cleared"),
          "info",
        ),
      );

    case "matchprop/apply": {
      const sourceEntityId = state.propertyWorkflows.matchpropSourceEntityId;

      if (!sourceEntityId) {
        return noShellMutation(
          pushAuditEvent(
            state,
            command,
            t("error.matchprop_no_source"),
            "warning",
          ),
        );
      }

      const targetEntityIds = command.targetEntityIds.filter(
        (entityId) => entityId !== sourceEntityId,
      );

      if (targetEntityIds.length === 0) {
        return noShellMutation(
          pushAuditEvent(
            state,
            command,
            t("error.matchprop_no_target"),
            "warning",
          ),
        );
      }

      const result = matchProperties({
        entities: state.drawing.entities,
        sourceEntityId,
        targetEntityIds,
        mode: command.mode,
      });

      const prevEntities = state.drawing.entities;
      const nextState = applyPropertyResults(
        state,
        command,
        [result],
        `MATCHPROP ${command.mode}`,
      );
      const mutations = buildPreviewMutations(
        prevEntities,
        nextState.drawing.entities,
        collectUpdatedIds([result]),
      );
      return { state: nextState, mutations };
    }

    default:
      return null;
  }
}
